const API_BASE = "/api/studio";

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: "An unexpected error occurred",
    }));
    throw new Error(error.message);
  }
  return response.json();
}

// --- Types ---

export interface StudioBlockNode {
  id: string;
  type: string;
  path: string | null;
  sortOrder: number;
  parentId: string | null;
  depth: number;
  status: string;
  metadata: Record<string, unknown>;
  preview: string | null;
}

export interface StudioBlockDetail extends StudioBlockNode {
  content: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface StudioTree {
  documentId: string;
  title: string;
  nodes: StudioBlockNode[];
}

export interface StudioSession {
  documentId: string;
  focusedBlockId: string | null;
  layout: Record<string, unknown>;
  collapsedIds: string[];
  pinnedIds: string[];
  viewMode: string;
  lastAccessed: string;
}

// --- API factory (takes an auth-aware fetch) ---

export function createStudioApi(fetchWithAuth: FetchFn) {
  return {
    // Tree
    getTree: async (docId: string): Promise<StudioTree> => {
      const res = await fetchWithAuth(`${API_BASE}/${docId}/tree`);
      return handleResponse<StudioTree>(res);
    },

    // Block CRUD
    getBlock: async (docId: string, blockId: string): Promise<StudioBlockDetail> => {
      const res = await fetchWithAuth(`${API_BASE}/${docId}/block/${blockId}`);
      return handleResponse<StudioBlockDetail>(res);
    },

    createBlock: async (docId: string, data: {
      type: string;
      content?: Record<string, unknown>;
      sortOrder?: number;
      parentId?: string;
      depth?: number;
    }): Promise<StudioBlockNode> => {
      const res = await fetchWithAuth(`${API_BASE}/${docId}/block`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      return handleResponse<StudioBlockNode>(res);
    },

    updateBlock: async (docId: string, blockId: string, data: {
      type?: string;
      content?: Record<string, unknown>;
    }): Promise<StudioBlockDetail> => {
      const res = await fetchWithAuth(`${API_BASE}/${docId}/block/${blockId}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
      return handleResponse<StudioBlockDetail>(res);
    },

    deleteBlock: async (docId: string, blockId: string): Promise<void> => {
      // SG-103 / #93: previously this swallowed non-2xx responses, so
      // a failed DELETE looked identical to a successful one — the
      // block stayed visible after loadTree() with no signal of why.
      // Throw on non-ok so the caller can show the error to the user.
      const res = await fetchWithAuth(`${API_BASE}/${docId}/block/${blockId}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`DELETE block failed: ${res.status} ${res.statusText}${text ? ` — ${text}` : ""}`);
      }
    },

    moveBlock: async (docId: string, blockId: string, data: {
      newParentId?: string | null;
      newPosition: number;
    }): Promise<void> => {
      await fetchWithAuth(`${API_BASE}/${docId}/block/${blockId}/move`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
    },

    updateMetadata: async (docId: string, blockId: string, data: {
      status?: string;
      metadata?: Record<string, unknown>;
    }): Promise<void> => {
      await fetchWithAuth(`${API_BASE}/${docId}/block/${blockId}/metadata`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
    },

    // Preview — single block. Fallback path; Studio prefetches the
    // whole document via getBlockPreviewsBatch below on mount.
    getBlockPreview: async (docId: string, blockId: string, format = "html"): Promise<string> => {
      const res = await fetchWithAuth(
        `${API_BASE}/${docId}/block/${blockId}/preview?format=${format}`
      );
      if (!res.ok) throw new Error("Preview failed");
      return res.text();
    },

    // Preview — batch. One request returns { blockId → rendered }.
    // Kills the N+1 that bit Studio: a 200-block doc used to fire
    // 200+ /block/{id}/preview calls. Now we fetch once, populate
    // a shared Map, and the per-card useEffect reads sync.
    getBlockPreviewsBatch: async (docId: string, format = "latex"): Promise<Record<string, string>> => {
      const res = await fetchWithAuth(
        `${API_BASE}/${docId}/blocks/previews?format=${format}`,
      );
      if (!res.ok) return {};
      return (await res.json()) as Record<string, string>;
    },

    renderBlockPreview: async (docId: string, blockId: string, format: string): Promise<void> => {
      await fetchWithAuth(`${API_BASE}/${docId}/block/${blockId}/preview`, {
        method: "POST",
        body: JSON.stringify({ format }),
      });
    },

    // Session
    getSession: async (docId: string): Promise<StudioSession | null> => {
      const res = await fetchWithAuth(`${API_BASE}/${docId}/session`);
      if (!res.ok) return null;
      const data = await res.json();
      if (!data.documentId) return null;
      return data as StudioSession;
    },

    saveSession: async (docId: string, data: {
      focusedBlockId?: string | null;
      layout?: Record<string, unknown>;
      collapsedIds?: string[];
      pinnedIds?: string[];
      viewMode?: string;
    }): Promise<StudioSession> => {
      const res = await fetchWithAuth(`${API_BASE}/${docId}/session`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
      return handleResponse<StudioSession>(res);
    },
    // LaTeX validation
    validateBlock: async (docId: string, blockId: string): Promise<{ valid: boolean; error?: string; warnings: string[] }> => {
      const res = await fetchWithAuth(`/api/latex/block/${blockId}/validate`, {
        method: "POST",
      });
      if (!res.ok) {
        return { valid: false, error: "Validation service unavailable", warnings: [] };
      }
      return res.json();
    },

    validateDocument: async (docId: string): Promise<{ valid: boolean; error?: string; warnings: string[]; durationMs?: number; engine?: string | null; pageCount?: number | null; log?: string }> => {
      const res = await fetchWithAuth(`/api/latex/${docId}/validate`, {
        method: "POST",
      });
      if (!res.ok) {
        return { valid: false, error: "Validation service unavailable", warnings: [] };
      }
      return res.json();
    },
  };
}

export type StudioApi = ReturnType<typeof createStudioApi>;
